import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useAuth } from '@/App';
import { useTheme } from '@/contexts/ThemeContext';
import RichTextEditor, { plainTextToHtml } from '@/components/RichTextEditor';
import ApiKeysPanel from '@/components/settings/ApiKeysPanel';

function SettingsPage() {
  const { user, refreshUser } = useAuth();
  const { theme, setTheme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('organization');
  
  const [settings, setSettings] = useState({
    // Organization
    company_name: '',
    company_description: '',
    company_website: '',
    company_phone: '',
    company_address: '',
    company_city: '',
    company_state: '',
    company_country: 'India',
    company_postal_code: '',
    company_timezone: 'Asia/Kolkata',
    // SMTP
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_password_set: false,
    smtp_auth_type: 'basic',
    authenticated_via_sso: false,
    // WhatsApp
    whatsapp_api_url: '',
    whatsapp_api_key: '',
    whatsapp_api_key_set: false,
    // SMS
    sms_api_url: '',
    sms_api_key: '',
    sms_api_key_set: false,
    // Outlook
    outlook_client_id: '',
    outlook_client_secret: '',
    outlook_tenant_id: '',
    outlook_connected: false,
  });
  const [microsoftConnecting, setMicrosoftConnecting] = useState(false);
  
  // Edit mode states for cards
  const [editingCard, setEditingCard] = useState(null);
  
  // Integration expansion state
  const [expandedIntegration, setExpandedIntegration] = useState('email');
  
  // Signatures state
  const [signatures, setSignatures] = useState([]);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [editingSignature, setEditingSignature] = useState(null);
  const [signatureForm, setSignatureForm] = useState({ name: '', content: '', is_default: false });
  const [savingSignature, setSavingSignature] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      if (response.data) {
        const isAuthenticatedViaSso = response.data.authenticated_via_sso === true;
        const smtpAuthType = isAuthenticatedViaSso ? 'microsoft_sso' : (response.data.smtp_auth_type || 'basic');
        
        setSettings(prev => ({
          ...prev,
          ...response.data,
          smtp_password: '',
          whatsapp_api_key: '',
          sms_api_key: '',
          smtp_auth_type: smtpAuthType,
          authenticated_via_sso: isAuthenticatedViaSso,
          outlook_connected: (smtpAuthType === 'oauth2' || smtpAuthType === 'microsoft_sso') || isAuthenticatedViaSso,
        }));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);
  
  const fetchSignatures = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/email/signatures`);
      setSignatures(response.data.signatures || []);
    } catch (error) {
      console.error('Error fetching signatures:', error);
    }
  }, []);
  
  useEffect(() => {
    if (activeTab === 'signatures') {
      fetchSignatures();
    }
  }, [activeTab, fetchSignatures]);

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    
    try {
      const dataToSave = { ...settings };
      if (!dataToSave.smtp_password) delete dataToSave.smtp_password;
      if (!dataToSave.whatsapp_api_key) delete dataToSave.whatsapp_api_key;
      if (!dataToSave.sms_api_key) delete dataToSave.sms_api_key;
      delete dataToSave.smtp_password_set;
      delete dataToSave.whatsapp_api_key_set;
      delete dataToSave.sms_api_key_set;
      
      await axios.post(`${API}/settings`, dataToSave);
      showMsg('success', 'Settings saved successfully');
      fetchSettings();
      setEditingCard(null);
    } catch (error) {
      showMsg('error', 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({ ...prev, [name]: value }));
  };

  const showMsg = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleTestSMTP = async () => {
    setTesting(true);
    try {
      const response = await axios.post(`${API}/settings/test-smtp`);
      if (response.data.success) {
        showMsg('success', 'SMTP connection successful!');
      } else {
        showMsg('error', response.data.message || 'SMTP test failed');
      }
    } catch (error) {
      showMsg('error', error.response?.data?.detail || 'Failed to test SMTP');
    } finally {
      setTesting(false);
    }
  };

  const handleMicrosoftConnect = async () => {
    setMicrosoftConnecting(true);
    try {
      // Validate required fields
      if (!settings.outlook_tenant_id || !settings.outlook_client_id || !settings.outlook_client_secret) {
        showMsg('error', 'Please fill in all required fields: Tenant ID, Client ID, and Client Secret');
        setMicrosoftConnecting(false);
        return;
      }
      
      // First, save the Microsoft SSO configuration to the backend
      const redirectUri = window.location.origin + '/auth/microsoft/callback';
      
      await axios.post(`${API}/auth/microsoft/config`, {
        client_id: settings.outlook_client_id,
        client_secret: settings.outlook_client_secret,
        tenant_id: settings.outlook_tenant_id,
        redirect_uri: redirectUri
      });
      
      // Also save user settings
      await handleSave();
      
      // Now get the Microsoft OAuth URL from the backend
      const response = await axios.get(`${API}/auth/microsoft/connect-url`, {
        params: { redirect_uri: redirectUri }
      });
      
      if (response.data.auth_url) {
        // Redirect to Microsoft login
        window.location.href = response.data.auth_url;
      } else {
        showMsg('error', 'Failed to get Microsoft authorization URL');
        setMicrosoftConnecting(false);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to initiate Microsoft connection';
      showMsg('error', errorMsg);
      setMicrosoftConnecting(false);
    }
  };

  const handleDisconnectMicrosoft = async () => {
    if (!window.confirm('Disconnect Microsoft 365? You will need to reconnect to use email features.')) return;
    
    try {
      await axios.post(`${API}/settings`, {
        ...settings,
        outlook_connected: false,
        smtp_auth_type: 'basic',
        outlook_client_id: '',
        outlook_client_secret: '',
        outlook_tenant_id: '',
      });
      fetchSettings();
      showMsg('success', 'Microsoft 365 disconnected');
    } catch (error) {
      showMsg('error', 'Failed to disconnect');
    }
  };
  
  // Microsoft SSO Status Component (for Email tab)
  const MicrosoftSSOStatus = () => {
    const isConnected = settings.authenticated_via_sso || settings.smtp_auth_type === 'microsoft_sso';
    
    return (
      <div className={`p-4 rounded-xl border ${isConnected ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isConnected ? 'bg-green-100 dark:bg-green-800/50' : 'bg-slate-200 dark:bg-slate-700'}`}>
              <svg className="w-6 h-6" viewBox="0 0 23 23">
                <path fill="#f35325" d="M0 0h11v11H0z"/>
                <path fill="#81bc06" d="M12 0h11v11H12z"/>
                <path fill="#05a6f0" d="M0 12h11v11H0z"/>
                <path fill="#ffba08" d="M12 12h11v11H12z"/>
              </svg>
            </div>
            <div>
              <p className={`font-semibold ${isConnected ? 'text-green-800 dark:text-green-300' : 'text-slate-700 dark:text-slate-300'}`}>
                Microsoft 365
              </p>
              <p className={`text-sm ${isConnected ? 'text-green-600 dark:text-green-400' : 'text-slate-500 dark:text-slate-400'}`}>
                {isConnected ? `Connected as ${user?.email}` : 'Not connected'}
              </p>
            </div>
          </div>
          <span className={`px-3 py-1 text-xs font-medium rounded-full ${
            isConnected ? 'bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300' : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
          }`}>
            {isConnected ? 'Active' : 'Inactive'}
          </span>
        </div>
      </div>
    );
  };
  
  // Calendar Sync Status Component (for Calendar tab)
  const CalendarSyncStatus = () => {
    const [syncStatus, setSyncStatus] = useState({ loading: true, connected: false, lastSync: null });
    
    useEffect(() => {
      const fetchSyncStatus = async () => {
        try {
          const response = await axios.get(`${API}/microsoft-sync/status`);
          setSyncStatus({
            loading: false,
            connected: response.data.calendar_sync_enabled || settings.smtp_auth_type === 'microsoft_sso' || settings.authenticated_via_sso,
            lastSync: response.data.last_calendar_sync
          });
        } catch (error) {
          // Fallback to settings-based check if sync endpoint fails
          setSyncStatus({
            loading: false,
            connected: settings.smtp_auth_type === 'microsoft_sso' || settings.authenticated_via_sso,
            lastSync: null
          });
        }
      };
      fetchSyncStatus();
    }, [settings.smtp_auth_type, settings.authenticated_via_sso]);
    
    if (syncStatus.loading) {
      return (
        <div className="p-4 rounded-xl border bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 animate-pulse">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-200 dark:bg-slate-700" />
            <div className="space-y-2">
              <div className="h-4 w-24 bg-slate-200 dark:bg-slate-700 rounded" />
              <div className="h-3 w-32 bg-slate-200 dark:bg-slate-700 rounded" />
            </div>
          </div>
        </div>
      );
    }
    
    const isConnected = syncStatus.connected;
    
    return (
      <div className={`p-4 rounded-xl border ${isConnected ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isConnected ? 'bg-green-100 dark:bg-green-800/50' : 'bg-slate-200 dark:bg-slate-700'}`}>
              <svg className="w-6 h-6" viewBox="0 0 23 23">
                <path fill="#f35325" d="M0 0h11v11H0z"/>
                <path fill="#81bc06" d="M12 0h11v11H12z"/>
                <path fill="#05a6f0" d="M0 12h11v11H0z"/>
                <path fill="#ffba08" d="M12 12h11v11H12z"/>
              </svg>
            </div>
            <div>
              <p className={`font-semibold ${isConnected ? 'text-green-800 dark:text-green-300' : 'text-slate-700 dark:text-slate-300'}`}>
                Microsoft Outlook Calendar
              </p>
              <p className={`text-sm ${isConnected ? 'text-green-600 dark:text-green-400' : 'text-slate-500 dark:text-slate-400'}`}>
                {isConnected 
                  ? syncStatus.lastSync 
                    ? `Last synced: ${new Date(syncStatus.lastSync).toLocaleDateString()}`
                    : `Connected as ${user?.email}` 
                  : 'Not connected - Connect via Email Integration first'}
              </p>
            </div>
          </div>
          <span className={`px-3 py-1 text-xs font-medium rounded-full ${
            isConnected ? 'bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300' : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
          }`}>
            {isConnected ? 'Active' : 'Inactive'}
          </span>
        </div>
        {isConnected && (
          <div className="mt-3 pt-3 border-t border-green-200 dark:border-green-800">
            <button
              onClick={async () => {
                try {
                  await axios.post(`${API}/microsoft-sync/calendar/sync`);
                  showMsg('success', 'Calendar sync started');
                } catch (error) {
                  showMsg('error', error.response?.data?.detail || 'Failed to sync calendar');
                }
              }}
              className="text-sm text-green-700 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 font-medium flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Sync Now
            </button>
          </div>
        )}
      </div>
    );
  };
  
  // Allowed Domains Manager Component
  const AllowedDomainsManager = () => {
    const [domains, setDomains] = useState([]);
    const [newDomain, setNewDomain] = useState('');
    const [loadingDomains, setLoadingDomains] = useState(true);
    
    useEffect(() => {
      fetchDomains();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    
    const fetchDomains = async () => {
      try {
        const response = await axios.get(`${API}/auth/sso/allowed-domains`);
        setDomains(response.data.domains || []);
      } catch (error) {
        console.error('Error fetching domains:', error);
      } finally {
        setLoadingDomains(false);
      }
    };
    
    const addDomain = async () => {
      if (!newDomain.trim()) return;
      try {
        await axios.post(`${API}/auth/sso/allowed-domains`, { domain: newDomain.trim().toLowerCase() });
        setNewDomain('');
        fetchDomains();
        showMsg('success', 'Domain added');
      } catch (error) {
        showMsg('error', error.response?.data?.detail || 'Failed to add domain');
      }
    };
    
    const removeDomain = async (domain) => {
      try {
        await axios.delete(`${API}/auth/sso/allowed-domains/${domain}`);
        fetchDomains();
        showMsg('success', 'Domain removed');
      } catch (error) {
        showMsg('error', 'Failed to remove domain');
      }
    };
    
    if (loadingDomains) {
      return <div className="animate-pulse h-32 bg-slate-100 dark:bg-slate-700 rounded-lg"></div>;
    }
    
    return (
      <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div className="px-5 py-3.5 border-b border-slate-100 dark:border-slate-700/60">
          <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Allowed Email Domains</h4>
          <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">Only users from these domains can register</p>
        </div>
        <div className="px-5 py-4">
          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              placeholder="e.g., intalk.io"
              className="flex-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-slate-400 dark:focus:ring-slate-500"
              onKeyPress={(e) => e.key === 'Enter' && addDomain()}
            />
            <button onClick={addDomain} className="px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-md hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white transition-colors">
              Add
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {domains.length === 0 ? (
              <p className="text-sm text-slate-400 dark:text-slate-500">No domains configured. All email domains allowed.</p>
            ) : (
              domains.map((domain) => (
                <span key={domain} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 dark:bg-slate-700/60 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 rounded-md text-xs">
                  @{domain}
                  <button onClick={() => removeDomain(domain)} className="text-slate-400 hover:text-rose-500 dark:hover:text-rose-400">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              ))
            )}
          </div>
        </div>
      </div>
    );
  };

  // ─── Profile Photo manager (per-user) ─────────────────────────────────
  const ProfilePhotoManager = () => {
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    const handleFile = async (file) => {
      if (!file) return;
      if (!file.type.startsWith('image/')) { showMsg('error', 'Please pick an image file'); return; }
      if (file.size > 2 * 1024 * 1024) { showMsg('error', 'Image too large — max 2 MB'); return; }
      setUploading(true);
      try {
        // Resize / compress in-browser to keep payload small (<= 512px square, JPEG q=0.85)
        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
              const max = 512;
              const ratio = Math.min(1, max / Math.max(img.width, img.height));
              const w = Math.round(img.width * ratio);
              const h = Math.round(img.height * ratio);
              const canvas = document.createElement('canvas');
              canvas.width = w; canvas.height = h;
              const ctx = canvas.getContext('2d');
              ctx.drawImage(img, 0, 0, w, h);
              resolve(canvas.toDataURL('image/jpeg', 0.85));
            };
            img.onerror = reject;
            img.src = e.target.result;
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        await axios.put(`${API}/auth/me/avatar`, { avatar_data_url: dataUrl });
        if (refreshUser) await refreshUser();
        showMsg('success', 'Photo updated');
      } catch (e) {
        showMsg('error', e?.response?.data?.detail || 'Failed to upload');
      } finally {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    };

    const removePhoto = async () => {
      if (!window.confirm('Remove your profile photo?')) return;
      setUploading(true);
      try {
        await axios.put(`${API}/auth/me/avatar`, { avatar_data_url: '' });
        if (refreshUser) await refreshUser();
        showMsg('success', 'Photo removed');
      } catch { showMsg('error', 'Failed to remove'); }
      finally { setUploading(false); }
    };

    const initial = (user?.name || '?').charAt(0).toUpperCase();
    const hasPhoto = !!user?.avatar_url;

    return (
      <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div className="px-5 py-3.5 border-b border-slate-100 dark:border-slate-700/60">
          <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Profile Photo</h4>
          <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">Shown in the sidebar and across the app</p>
        </div>
        <div className="px-5 py-4 flex items-center gap-4">
          {hasPhoto ? (
            <img
              src={user.avatar_url}
              alt={user.name}
              className="w-16 h-16 rounded-xl object-cover border border-slate-200 dark:border-slate-700 shadow-sm"
              data-testid="profile-photo-preview"
            />
          ) : (
            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center text-white font-bold text-2xl shadow-sm">
              {initial}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{user?.name}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">JPG / PNG / WebP · max 2 MB · resized to 512px</p>
            <div className="flex gap-2 mt-3">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
                data-testid="profile-photo-input"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="px-3 py-1.5 bg-slate-900 text-white text-xs font-medium rounded-md hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white disabled:opacity-50 transition-colors"
                data-testid="profile-photo-upload"
              >
                {uploading ? 'Uploading…' : (hasPhoto ? 'Change photo' : 'Upload photo')}
              </button>
              {hasPhoto && (
                <button
                  onClick={removePhoto}
                  disabled={uploading}
                  className="px-3 py-1.5 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 text-xs font-medium rounded-md hover:bg-slate-50 dark:hover:bg-slate-700/40 disabled:opacity-50 transition-colors"
                  data-testid="profile-photo-remove"
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleSaveSignature = async () => {
    if (!signatureForm.name || !signatureForm.content) {
      showMsg('error', 'Please enter signature name and content');
      return;
    }
    
    setSavingSignature(true);
    try {
      if (editingSignature) {
        await axios.put(`${API}/email/signatures/${editingSignature.id}`, signatureForm);
      } else {
        await axios.post(`${API}/email/signatures`, signatureForm);
      }
      fetchSignatures();
      setShowSignatureModal(false);
      setEditingSignature(null);
      setSignatureForm({ name: '', content: '', is_default: false });
      showMsg('success', 'Signature saved successfully');
    } catch (error) {
      showMsg('error', 'Failed to save signature');
    } finally {
      setSavingSignature(false);
    }
  };
  
  const handleDeleteSignature = async (sigId) => {
    if (!window.confirm('Delete this signature?')) return;
    
    try {
      await axios.delete(`${API}/email/signatures/${sigId}`);
      fetchSignatures();
      showMsg('success', 'Signature deleted');
    } catch (error) {
      showMsg('error', 'Failed to delete signature');
    }
  };
  
  const openEditSignature = (sig) => {
    setEditingSignature(sig);
    // Auto-upgrade legacy plain-text signatures to HTML so the rich editor
    // renders paragraphs + lists + bold correctly. HTML content passes through.
    setSignatureForm({ name: sig.name, content: plainTextToHtml(sig.content || ''), is_default: sig.is_default });
    setShowSignatureModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="settings-loading">
        <div className="animate-spin w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'organization', label: 'Organization', icon: 'building' },
    { id: 'integrations', label: 'Integrations', icon: 'plug' },
    { id: 'automation', label: 'Automation', icon: 'zap' },
    { id: 'signatures', label: 'Signatures', icon: 'signature' },
    { id: 'calendar', label: 'Calendar', icon: 'calendar' },
    ...(user?.role === 'admin' ? [{ id: 'api_keys', label: 'API Keys', icon: 'key' }] : []),
  ];

  // Card Component for Organization Settings
  const SettingsCard = ({ title, description, children, cardId, onEdit }) => (
    <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
      <div className="px-5 py-3.5 border-b border-slate-100 dark:border-slate-700/60 flex items-center justify-between">
        <div className="min-w-0">
          <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">{title}</h4>
          {description && <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">{description}</p>}
        </div>
        {onEdit && editingCard !== cardId && (
          <button 
            onClick={() => setEditingCard(cardId)}
            className="text-xs font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white inline-flex items-center gap-1.5 px-2 py-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700/60"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
            Edit
          </button>
        )}
      </div>
      <div className="px-5 py-4">
        {children}
      </div>
    </div>
  );

  // Integration Card Component
  const IntegrationCard = ({ title, description, icon, iconBg, status, statusColor, children, isExpanded, onToggle }) => (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
      <div 
        className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 transition"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 ${iconBg} rounded-lg flex items-center justify-center`}>
            {icon}
          </div>
          <div>
            <h4 className="font-semibold text-slate-800 dark:text-slate-100">{title}</h4>
            <p className="text-xs text-slate-500 dark:text-slate-400">{description}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`px-3 py-1 text-xs font-medium rounded-full ${statusColor}`}>
            {status}
          </span>
          <svg className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
      {isExpanded && (
        <div className="p-4 border-t border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
          {children}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Settings</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Manage your organization and integrations</p>
        </div>
      </div>

      {message.text && (
        <div
          className={`px-4 py-3 rounded-lg flex items-center gap-2 ${
            message.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-700'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}
          data-testid="settings-message"
        >
          {message.type === 'success' ? (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          )}
          {message.text}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-slate-200 dark:border-slate-700">
        <nav className="flex space-x-1 overflow-x-auto scrollbar-hide" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'text-slate-900 dark:text-white border-slate-900 dark:border-white'
                  : 'text-slate-500 dark:text-slate-400 border-transparent hover:text-slate-700 dark:hover:text-slate-200 hover:border-slate-300 dark:hover:border-slate-600'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              {tab.icon === 'building' && (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              )}
              {tab.icon === 'plug' && (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
              )}
              {tab.icon === 'zap' && (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              )}
              {tab.icon === 'signature' && (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              )}
              {tab.icon === 'calendar' && (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              )}
              {tab.icon === 'key' && (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
              )}
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Organization Tab */}
      {activeTab === 'organization' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="organization-settings">
          {/* Company Profile Card */}
          <SettingsCard
            title="Company Profile"
            description="Your organization details"
            cardId="company"
            onEdit={true}
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            }
          >
            {editingCard === 'company' ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Company Name</label>
                  <input
                    type="text"
                    name="company_name"
                    value={settings.company_name}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="intalk.io"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Description</label>
                  <textarea
                    name="company_description"
                    value={settings.company_description}
                    onChange={handleChange}
                    rows={2}
                    className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    placeholder="SDR CRM Platform"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Website</label>
                    <input
                      type="url"
                      name="company_website"
                      value={settings.company_website}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="https://intalk.io"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Phone</label>
                    <input
                      type="tel"
                      name="company_phone"
                      value={settings.company_phone}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="+91 XXXXX XXXXX"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button onClick={() => setEditingCard(null)} className="px-3 py-1.5 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200">
                    Cancel
                  </button>
                  <button onClick={handleSave} disabled={saving} className="px-4 py-1.5 bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-sm rounded-lg hover:bg-slate-800 dark:hover:bg-white disabled:opacity-50">
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg flex items-center justify-center text-slate-700 dark:text-slate-200 font-semibold">
                    {(settings.company_name || 'C')[0].toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900 dark:text-white truncate">{settings.company_name || 'Your Company'}</p>
                    <p className="text-sm text-slate-500 dark:text-slate-400 truncate">{settings.company_description || 'No description'}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 pt-2">
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wide">Website</p>
                    <p className="text-sm text-slate-700 dark:text-slate-300">{settings.company_website || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wide">Phone</p>
                    <p className="text-sm text-slate-700 dark:text-slate-300">{settings.company_phone || '-'}</p>
                  </div>
                </div>
              </div>
            )}
          </SettingsCard>

          {/* Business Address Card */}
          <SettingsCard
            title="Business Address"
            description="Your company location"
            cardId="address"
            onEdit={true}
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            }
          >
            {editingCard === 'address' ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Street Address</label>
                  <input
                    type="text"
                    name="company_address"
                    value={settings.company_address}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="123 Business Street"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">City</label>
                    <input
                      type="text"
                      name="company_city"
                      value={settings.company_city}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Mumbai"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">State</label>
                    <input
                      type="text"
                      name="company_state"
                      value={settings.company_state}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Maharashtra"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Country</label>
                    <input
                      type="text"
                      name="company_country"
                      value={settings.company_country}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="India"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Postal Code</label>
                    <input
                      type="text"
                      name="company_postal_code"
                      value={settings.company_postal_code}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="400001"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button onClick={() => setEditingCard(null)} className="px-3 py-1.5 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200">
                    Cancel
                  </button>
                  <button onClick={handleSave} disabled={saving} className="px-4 py-1.5 bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-sm rounded-lg hover:bg-slate-800 dark:hover:bg-white disabled:opacity-50">
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wide">Country</p>
                    <p className="text-sm text-slate-700 dark:text-slate-300">{settings.company_country || 'India'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wide">State</p>
                    <p className="text-sm text-slate-700 dark:text-slate-300">{settings.company_state || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wide">City</p>
                    <p className="text-sm text-slate-700 dark:text-slate-300">{settings.company_city || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wide">Postal Code</p>
                    <p className="text-sm text-slate-700 dark:text-slate-300">{settings.company_postal_code || '-'}</p>
                  </div>
                </div>
                {settings.company_address && (
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wide">Street</p>
                    <p className="text-sm text-slate-700 dark:text-slate-300">{settings.company_address}</p>
                  </div>
                )}
              </div>
            )}
          </SettingsCard>

          {/* Account Info Card */}
          <SettingsCard
            title="Account Information"
            description="Your login details"
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            }
          >
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wide">Email</p>
                  <p className="text-sm text-slate-700 dark:text-slate-300">{user?.email || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wide">Role</p>
                  <p className="text-sm text-slate-700 dark:text-slate-300 capitalize">{user?.role || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wide">Timezone</p>
                  <p className="text-sm text-slate-700 dark:text-slate-300">{settings.company_timezone || 'Asia/Kolkata'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wide">Login Method</p>
                  <p className="text-sm text-slate-700 dark:text-slate-300">
                    {settings.authenticated_via_sso ? 'Microsoft SSO' : 'Email/Password'}
                  </p>
                </div>
              </div>
            </div>
          </SettingsCard>

          {/* Allowed Domains Card */}
          <AllowedDomainsManager />

          {/* Profile Photo Card (per-user) */}
          <ProfilePhotoManager />

          {/* Theme Settings Card */}
          <SettingsCard
            title="Appearance"
            description="Customize the look and feel"
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            }
          >
            <div className="space-y-3">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400 mb-2">Theme</p>
                <div className="grid grid-cols-3 gap-2">
                  {/* Light Theme Option */}
                  <button
                    onClick={() => setTheme('light')}
                    className={`px-3 py-3 rounded-md border text-left transition-colors ${
                      theme === 'light' 
                        ? 'border-slate-900 dark:border-slate-200 bg-slate-50 dark:bg-slate-700/50' 
                        : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                    }`}
                    data-testid="theme-light"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-md bg-white border border-slate-200 flex items-center justify-center shadow-sm shrink-0">
                        <svg className="w-4 h-4 text-amber-500" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM7.5 12a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM18.894 6.166a.75.75 0 00-1.06-1.06l-1.591 1.59a.75.75 0 101.06 1.061l1.591-1.59zM21.75 12a.75.75 0 01-.75.75h-2.25a.75.75 0 010-1.5H21a.75.75 0 01.75.75zM17.834 18.894a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 10-1.061 1.06l1.59 1.591zM12 18a.75.75 0 01.75.75V21a.75.75 0 01-1.5 0v-2.25A.75.75 0 0112 18zM7.758 17.303a.75.75 0 00-1.061-1.06l-1.591 1.59a.75.75 0 001.06 1.061l1.591-1.59zM6 12a.75.75 0 01-.75.75H3a.75.75 0 010-1.5h2.25A.75.75 0 016 12zM6.697 7.757a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 00-1.061 1.06l1.59 1.591z" />
                        </svg>
                      </div>
                      <div className="min-w-0">
                        <span className="block text-sm font-medium text-slate-700 dark:text-slate-200">Light</span>
                        {theme === 'light' && <span className="block text-[10px] text-slate-500 dark:text-slate-400">Active</span>}
                      </div>
                    </div>
                  </button>

                  {/* Dark Theme Option */}
                  <button
                    onClick={() => setTheme('dark')}
                    className={`px-3 py-3 rounded-md border text-left transition-colors ${
                      theme === 'dark' 
                        ? 'border-slate-900 dark:border-slate-200 bg-slate-50 dark:bg-slate-700/50' 
                        : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                    }`}
                    data-testid="theme-dark"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-md bg-slate-900 flex items-center justify-center shrink-0">
                        <svg className="w-4 h-4 text-slate-300" fill="currentColor" viewBox="0 0 24 24">
                          <path fillRule="evenodd" d="M9.528 1.718a.75.75 0 01.162.819A8.97 8.97 0 009 6a9 9 0 009 9 8.97 8.97 0 003.463-.69.75.75 0 01.981.98 10.503 10.503 0 01-9.694 6.46c-5.799 0-10.5-4.701-10.5-10.5 0-4.368 2.667-8.112 6.46-9.694a.75.75 0 01.818.162z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="min-w-0">
                        <span className="block text-sm font-medium text-slate-700 dark:text-slate-200">Dark</span>
                        {theme === 'dark' && <span className="block text-[10px] text-slate-500 dark:text-slate-400">Active</span>}
                      </div>
                    </div>
                  </button>

                  {/* System Theme Option */}
                  <button
                    onClick={() => setTheme('system')}
                    className={`px-3 py-3 rounded-md border text-left transition-colors ${
                      theme === 'system' 
                        ? 'border-slate-900 dark:border-slate-200 bg-slate-50 dark:bg-slate-700/50' 
                        : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                    }`}
                    data-testid="theme-system"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-md bg-gradient-to-br from-white to-slate-800 border border-slate-200 flex items-center justify-center shrink-0">
                        <svg className="w-4 h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div className="min-w-0">
                        <span className="block text-sm font-medium text-slate-700 dark:text-slate-200">System</span>
                        {theme === 'system' && <span className="block text-[10px] text-slate-500 dark:text-slate-400">Active</span>}
                      </div>
                    </div>
                  </button>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-3">
                  {theme === 'system' 
                    ? 'Theme will follow your system preferences' 
                    : theme === 'dark'
                    ? 'Dark theme is always active'
                    : 'Light theme is always active'
                  }
                </p>
              </div>
            </div>
          </SettingsCard>
        </div>
      )}

      {/* Integrations Tab */}
      {activeTab === 'integrations' && (
        <div className="space-y-4" data-testid="integrations-settings">
          {/* Email Integration */}
          <IntegrationCard
            title="Email (SMTP)"
            description="Send emails from your CRM"
            icon={<MailIcon className="w-5 h-5 text-blue-600" />}
            iconBg="bg-blue-100"
            status={settings.authenticated_via_sso || settings.outlook_connected ? 'Connected' : settings.smtp_host ? 'Configured' : 'Not configured'}
            statusColor={settings.authenticated_via_sso || settings.outlook_connected ? 'bg-green-100 text-green-700' : settings.smtp_host ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}
            isExpanded={expandedIntegration === 'email'}
            onToggle={() => setExpandedIntegration(expandedIntegration === 'email' ? null : 'email')}
          >
            <div className="space-y-4">
              {/* Microsoft SSO Banner */}
              {(settings.smtp_auth_type === 'microsoft_sso' || settings.authenticated_via_sso) && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white dark:bg-slate-900 rounded-lg flex items-center justify-center shadow-sm">
                      <svg className="w-6 h-6" viewBox="0 0 23 23">
                        <path fill="#f35325" d="M0 0h11v11H0z"/>
                        <path fill="#81bc06" d="M12 0h11v11H12z"/>
                        <path fill="#05a6f0" d="M0 12h11v11H0z"/>
                        <path fill="#ffba08" d="M12 12h11v11H12z"/>
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-green-800">Email Auto-configured via Microsoft 365</p>
                      <p className="text-sm text-green-600">Emails will be sent from {user?.email}</p>
                    </div>
                    <svg className="w-6 h-6 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              )}

              {/* Auth Method Selector - Only show if not SSO */}
              {!settings.authenticated_via_sso && settings.smtp_auth_type !== 'microsoft_sso' && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div
                      onClick={() => setSettings(prev => ({ ...prev, smtp_auth_type: 'basic' }))}
                      className={`p-3 border-2 rounded-lg cursor-pointer transition ${
                        settings.smtp_auth_type === 'basic' || !settings.smtp_auth_type
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <input type="radio" checked={settings.smtp_auth_type === 'basic' || !settings.smtp_auth_type} onChange={() => {}} className="h-4 w-4 text-blue-600" />
                        <span className="font-medium text-sm text-slate-900 dark:text-white">Basic SMTP</span>
                      </div>
                      <p className="text-xs text-slate-500">Username/password authentication</p>
                    </div>
                    <div
                      onClick={() => setSettings(prev => ({ ...prev, smtp_auth_type: 'oauth2' }))}
                      className={`p-3 border-2 rounded-lg cursor-pointer transition ${
                        settings.smtp_auth_type === 'oauth2'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <input type="radio" checked={settings.smtp_auth_type === 'oauth2'} onChange={() => {}} className="h-4 w-4 text-blue-600" />
                        <span className="font-medium text-sm text-slate-900 dark:text-white">Microsoft 365</span>
                      </div>
                      <p className="text-xs text-slate-500">OAuth2 for work accounts</p>
                    </div>
                  </div>

                  {/* Basic SMTP Fields */}
                  {(settings.smtp_auth_type === 'basic' || !settings.smtp_auth_type) && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">SMTP Host</label>
                        <input type="text" name="smtp_host" value={settings.smtp_host} onChange={handleChange} placeholder="smtp.office365.com" className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white" data-testid="smtp-host-input" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Port</label>
                        <input type="number" name="smtp_port" value={settings.smtp_port} onChange={handleChange} placeholder="587" className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white" data-testid="smtp-port-input" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Username</label>
                        <input type="email" name="smtp_username" value={settings.smtp_username} onChange={handleChange} placeholder="email@domain.com" className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white" data-testid="smtp-username-input" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Password {settings.smtp_password_set && <span className="text-green-600">(saved)</span>}</label>
                        <input type="password" name="smtp_password" value={settings.smtp_password} onChange={handleChange} placeholder="••••••••" className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white" data-testid="smtp-password-input" />
                      </div>
                    </div>
                  )}

                  {/* Microsoft 365 OAuth Fields */}
                  {settings.smtp_auth_type === 'oauth2' && (
                    <div className="space-y-4">
                      {settings.outlook_connected ? (
                        <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span className="text-sm text-green-700 dark:text-green-300">Microsoft 365 Connected</span>
                          </div>
                          <button onClick={handleDisconnectMicrosoft} className="text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300">Disconnect</button>
                        </div>
                      ) : (
                        <>
                          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                            <p className="text-xs text-blue-700 dark:text-blue-300">
                              <strong>Setup Instructions:</strong> Go to Azure Portal → App Registrations → Create app → Copy Client ID, Tenant ID, and create a Client Secret.
                              <a href="https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps/ApplicationsListBlade" target="_blank" rel="noopener noreferrer" className="ml-1 underline">Open Azure Portal →</a>
                            </p>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Tenant ID <span className="text-red-500">*</span></label>
                              <input type="text" name="outlook_tenant_id" value={settings.outlook_tenant_id || ''} onChange={handleChange} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white" />
                              <p className="text-xs text-slate-400 mt-1">From Azure AD → Overview</p>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Client ID <span className="text-red-500">*</span></label>
                              <input type="text" name="outlook_client_id" value={settings.outlook_client_id || ''} onChange={handleChange} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white" />
                              <p className="text-xs text-slate-400 mt-1">Application (client) ID</p>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Client Secret <span className="text-red-500">*</span></label>
                              <input type="password" name="outlook_client_secret" value={settings.outlook_client_secret || ''} onChange={handleChange} placeholder="••••••••" className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white" />
                              <p className="text-xs text-slate-400 mt-1">Certificates & secrets → New</p>
                            </div>
                          </div>
                          <button 
                            onClick={handleMicrosoftConnect} 
                            disabled={!settings.outlook_client_id || !settings.outlook_tenant_id || !settings.outlook_client_secret || microsoftConnecting} 
                            className="w-full py-2.5 bg-slate-900 dark:bg-slate-700 text-white rounded-lg text-sm font-medium hover:bg-slate-800 dark:hover:bg-slate-600 disabled:opacity-50 flex items-center justify-center gap-2"
                          >
                            {microsoftConnecting ? 'Connecting...' : 'Connect Microsoft 365'}
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* Test & Save Buttons */}
              {!settings.authenticated_via_sso && settings.smtp_auth_type !== 'microsoft_sso' && (
                <div className="flex justify-end gap-2 pt-2">
                  <button onClick={handleTestSMTP} disabled={testing} className="px-4 py-2 border border-slate-200 text-slate-700 rounded-lg text-sm hover:bg-slate-50 disabled:opacity-50" data-testid="test-smtp-btn">
                    {testing ? 'Testing...' : 'Test Connection'}
                  </button>
                  <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-slate-900 text-white rounded-lg text-sm hover:bg-slate-800 disabled:opacity-50" data-testid="save-smtp-btn">
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              )}
            </div>
          </IntegrationCard>

          {/* WhatsApp Integration */}
          <IntegrationCard
            title="WhatsApp Business"
            description="Send WhatsApp messages to leads"
            icon={<ChatIcon className="w-5 h-5 text-green-600" />}
            iconBg="bg-green-100"
            status={settings.whatsapp_api_url ? 'Configured' : 'Not configured'}
            statusColor={settings.whatsapp_api_url ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}
            isExpanded={expandedIntegration === 'whatsapp'}
            onToggle={() => setExpandedIntegration(expandedIntegration === 'whatsapp' ? null : 'whatsapp')}
          >
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">API Endpoint URL</label>
                <input type="url" name="whatsapp_api_url" value={settings.whatsapp_api_url} onChange={handleChange} placeholder="https://api.whatsapp-provider.com/v1/send" className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white" data-testid="whatsapp-url-input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">API Key {settings.whatsapp_api_key_set && <span className="text-green-600">(saved)</span>}</label>
                <input type="password" name="whatsapp_api_key" value={settings.whatsapp_api_key} onChange={handleChange} placeholder="••••••••" className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white" data-testid="whatsapp-key-input" />
              </div>
              <div className="flex justify-end">
                <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-slate-900 dark:bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 rounded-lg text-sm hover:bg-slate-800 dark:hover:bg-slate-800 dark:hover:bg-white disabled:opacity-50">
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </IntegrationCard>

          {/* SMS Integration */}
          <IntegrationCard
            title="SMS Gateway"
            description="Send SMS to leads"
            icon={<PhoneIcon className="w-5 h-5 text-orange-600" />}
            iconBg="bg-orange-100"
            status={settings.sms_api_url ? 'Configured' : 'Not configured'}
            statusColor={settings.sms_api_url ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}
            isExpanded={expandedIntegration === 'sms'}
            onToggle={() => setExpandedIntegration(expandedIntegration === 'sms' ? null : 'sms')}
          >
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">API Endpoint URL</label>
                <input type="url" name="sms_api_url" value={settings.sms_api_url} onChange={handleChange} placeholder="https://api.sms-provider.com/v1/send" className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white" data-testid="sms-url-input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">API Key {settings.sms_api_key_set && <span className="text-green-600">(saved)</span>}</label>
                <input type="password" name="sms_api_key" value={settings.sms_api_key} onChange={handleChange} placeholder="••••••••" className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white" data-testid="sms-key-input" />
              </div>
              <div className="flex justify-end">
                <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-slate-900 dark:bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 rounded-lg text-sm hover:bg-slate-800 dark:hover:bg-slate-800 dark:hover:bg-white disabled:opacity-50">
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </IntegrationCard>
        </div>
      )}

      {/* Automation Tab - Smart Follow-up Rules */}
      {activeTab === 'automation' && (
        <SmartFollowupSettings />
      )}

      {/* Signatures Tab */}
      {activeTab === 'signatures' && (
        <div className="space-y-6" data-testid="signatures-settings">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Email Signatures</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">Create and manage your email signatures</p>
            </div>
            <button
              onClick={() => {
                setEditingSignature(null);
                setSignatureForm({ name: '', content: '', is_default: false });
                setShowSignatureModal(true);
              }}
              className="px-4 py-2 bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-sm rounded-lg hover:bg-slate-800 dark:hover:bg-white flex items-center gap-2"
              data-testid="add-signature-btn"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Signature
            </button>
          </div>

          {signatures.length === 0 ? (
            <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
              <svg className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
              <p className="text-slate-500 dark:text-slate-400 mb-4">No signatures created yet</p>
              <button
                onClick={() => {
                  setEditingSignature(null);
                  setSignatureForm({ name: '', content: '', is_default: false });
                  setShowSignatureModal(true);
                }}
                className="px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 text-sm"
              >
                Create your first signature
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {signatures.map((sig) => (
                <div key={sig.id} className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 transition">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-slate-800 dark:text-slate-100">{sig.name}</h4>
                      {sig.is_default && (
                        <span className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full">Default</span>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => openEditSignature(sig)} className="p-1.5 text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded" data-testid={`edit-sig-${sig.id}`}>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button onClick={() => handleDeleteSignature(sig.id)} className="p-1.5 text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded" data-testid={`delete-sig-${sig.id}`}>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div className="text-sm text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-900 p-3 rounded-lg whitespace-pre-wrap line-clamp-4 [&_*]:!text-inherit [&_*]:!bg-transparent" dangerouslySetInnerHTML={{ __html: sig.content.replace(/\n/g, '<br/>') }} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Calendar Tab */}
      {activeTab === 'calendar' && (
        <div className="space-y-6" data-testid="calendar-settings">
          <div>
            <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Microsoft Outlook Calendar</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">Connect your Microsoft 365 calendar for scheduling</p>
          </div>

          <CalendarSyncStatus />

          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
            <h4 className="font-semibold text-slate-800 dark:text-slate-100 mb-3">How It Works</h4>
            <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
              <li className="flex items-start gap-2">
                <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Automatic .ics calendar attachment in meeting invite emails
              </li>
              <li className="flex items-start gap-2">
                <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Outlook Calendar sync for scheduled meetings
              </li>
              <li className="flex items-start gap-2">
                <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Meeting reminders via Microsoft 365
              </li>
            </ul>
          </div>

          <AllowedDomainsManager />
        </div>
      )}
      
      {/* Signature Modal */}
      {showSignatureModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-lg">
            <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{editingSignature ? 'Edit Signature' : 'New Signature'}</h2>
              <button onClick={() => { setShowSignatureModal(false); setEditingSignature(null); }} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Signature Name</label>
                <input
                  type="text"
                  value={signatureForm.name}
                  onChange={(e) => setSignatureForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Professional, Casual"
                  data-testid="signature-name-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Signature Content</label>
                <RichTextEditor
                  value={signatureForm.content}
                  onChange={(html) => setSignatureForm(prev => ({ ...prev, content: html }))}
                  placeholder="Best regards, Your Name — Role — Company · Phone · Website"
                  minHeight="220px"
                />
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Use the toolbar for bold name, links, colors, and the <span className="font-medium">image icon</span> to add a banner (paste a hosted URL of your logo/banner image).
                </p>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_default"
                  checked={signatureForm.is_default}
                  onChange={(e) => setSignatureForm(prev => ({ ...prev, is_default: e.target.checked }))}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <label htmlFor="is_default" className="text-sm text-slate-600 dark:text-slate-400">Set as default signature</label>
              </div>
            </div>
            
            <div className="p-4 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-3">
              <button onClick={() => { setShowSignatureModal(false); setEditingSignature(null); }} className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200">
                Cancel
              </button>
              <button onClick={handleSaveSignature} disabled={savingSignature} className="px-4 py-2 bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-sm rounded-lg hover:bg-slate-800 dark:hover:bg-white disabled:opacity-50" data-testid="save-signature-btn">
                {savingSignature ? 'Saving...' : 'Save Signature'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* API Keys Tab (admin only) */}
      {activeTab === 'api_keys' && user?.role === 'admin' && (
        <ApiKeysPanel />
      )}
    </div>
  );
}

// Icon Components
function MailIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );
}

function ChatIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  );
}

function PhoneIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  );
}

export default SettingsPage;

// Smart Follow-up Settings Component
function SmartFollowupSettings() {
  const [rules, setRules] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    const fetchRules = async () => {
      try {
        const res = await axios.get(`${API}/settings/followup-rules`);
        setRules(res.data);
      } catch (err) {
        console.error('Error fetching followup rules:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchRules();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setMsg('');
    try {
      await axios.put(`${API}/settings/followup-rules`, rules);
      setMsg('Rules saved successfully');
      setTimeout(() => setMsg(''), 3000);
    } catch (err) {
      setMsg('Failed to save rules');
    } finally {
      setSaving(false);
    }
  };

  const handleRunNow = async () => {
    setRunning(true);
    setLastResult(null);
    try {
      const res = await axios.post(`${API}/settings/followup-rules/run-now`);
      setLastResult(res.data.result);
    } catch (err) {
      console.error('Error running check:', err);
    } finally {
      setRunning(false);
    }
  };

  if (loading || !rules) {
    return <div className="animate-pulse h-40 bg-slate-100 dark:bg-slate-800 rounded-xl"></div>;
  }

  const RuleRow = ({ label, description, enabledKey, daysKey, daysLabel }) => (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100">{label}</h4>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{description}</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={rules[enabledKey] || false}
            onChange={(e) => setRules(prev => ({ ...prev, [enabledKey]: e.target.checked }))}
            className="sr-only peer"
            data-testid={`toggle-${enabledKey}`}
          />
          <div className="w-9 h-5 bg-slate-300 dark:bg-slate-600 peer-focus:ring-2 peer-focus:ring-blue-400 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
        </label>
      </div>
      {rules[enabledKey] && daysKey && (
        <div className="flex items-center gap-3">
          <label className="text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">{daysLabel}</label>
          <input
            type="number"
            min="1"
            max="90"
            value={rules[daysKey] || ''}
            onChange={(e) => setRules(prev => ({ ...prev, [daysKey]: parseInt(e.target.value) || 1 }))}
            className="w-20 px-3 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
            data-testid={`input-${daysKey}`}
          />
          <span className="text-xs text-slate-400">days</span>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="automation-settings">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Smart Follow-ups</h3>
          <p className="text-sm text-slate-500 dark:text-slate-400">Auto-create tasks when leads or deals need attention</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleRunNow}
            disabled={running}
            className="px-3 py-1.5 text-sm font-medium border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 disabled:opacity-50 transition-colors"
            data-testid="run-followup-now"
          >
            {running ? 'Running...' : 'Run Now'}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-1.5 text-sm font-medium bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-lg hover:bg-slate-800 dark:hover:bg-slate-200 disabled:opacity-50 transition-colors"
            data-testid="save-followup-rules"
          >
            {saving ? 'Saving...' : 'Save Rules'}
          </button>
        </div>
      </div>

      {msg && (
        <div className="px-3 py-2 text-sm bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded-lg">{msg}</div>
      )}

      {lastResult && (
        <div className="px-4 py-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <p className="text-sm font-medium text-blue-800 dark:text-blue-300">Check completed</p>
          <div className="flex gap-4 mt-1 text-xs text-blue-600 dark:text-blue-400">
            <span>Stale leads: {lastResult.stale_leads} tasks</span>
            <span>Aging deals: {lastResult.aging_opportunities} tasks</span>
            <span>Post-meeting: {lastResult.post_meeting} tasks</span>
            <span className="font-bold">Total: {lastResult.total}</span>
          </div>
        </div>
      )}

      <div className="space-y-3">
        <RuleRow
          label="Stale Lead Follow-up"
          description="Auto-create a follow-up task when a lead has no activity"
          enabledKey="stale_lead_enabled"
          daysKey="stale_lead_days"
          daysLabel="Trigger after"
        />
        <RuleRow
          label="Aging Opportunity Alert"
          description="Auto-create a task when a deal is stuck in the same stage"
          enabledKey="aging_opportunity_enabled"
          daysKey="aging_opportunity_days"
          daysLabel="Trigger after"
        />
        <RuleRow
          label="Post-Meeting Follow-up"
          description="Auto-create a task when no follow-up happens after a meeting/call"
          enabledKey="post_meeting_enabled"
          daysKey="post_meeting_followup_days"
          daysLabel="Trigger after"
        />
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Background Check Interval</h4>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">How often the system checks for needed follow-ups</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">Every</span>
            <input
              type="number"
              min="5"
              max="1440"
              value={rules.check_interval_minutes || 30}
              onChange={(e) => setRules(prev => ({ ...prev, check_interval_minutes: parseInt(e.target.value) || 30 }))}
              className="w-20 px-3 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
              data-testid="input-check-interval"
            />
            <span className="text-xs text-slate-400">minutes</span>
          </div>
        </div>
      </div>
    </div>
  );
}
